<template>
    <div class="grid gap-4 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-[20%_80%] xl:grid-cols-[23%_75%]">
        <div class="w-full" id="strategy-article-filters-container">
            <DefaultSkeleton v-if="isLoading" class="mb-10"/>
            <ListSkeleton v-if="isLoading" class="hidden sm:block"/>
            <Filters
                id="filter-sidebar"
                class-list="automation-search-wrapper flex flex-col"
                :initial-filters="this.filters"
                :articles="this.strategyArticles"
                :filter-icon="this.filterIcon"
                @input="getFilteredArticles"
                v-if="!isLoading"/>
        </div>
        <div class="grid gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-[32.3%_32.3%_32.3%] xl:grid-cols-[32.3%_32.3%_32.3%]">

            <ArticleCardSkeleton v-for="index in this.perPage" :key="index" v-if="isLoading"/>

            <ArticleCard v-for="article in articles" :key="article.id" :article="article"
                         v-if="!isLoading && articles.length > 0"></ArticleCard>

            <Warning v-if="!isLoading && articles.length === 0" class="col-span-full gap-0 text-center">
                {{ noArticlesLabel }}
            </Warning>

            <!-- skeleton for pagination -->
            <PaginationSkeleton v-if="isLoading" class="col-span-full gap-0 w-full mx-auto"/>

            <Pagination
                class="col-span-full gap-0"
                v-if="!isLoading && articles.length > 0"
                :total-pages="this.articlesPerPage"
                :total="this.filteredStrategyArticles.length"
                :per-page="this.perPage"
                :current-page="this.currentPage"
                @pagechanged="(page) => changePage(page)"/>
        </div>
    </div>
</template>

<script type="text/javascript">
import ListSkeleton from "../Layout/Skeletons/ListSkeleton";
import DefaultSkeleton from "../Layout/Skeletons/DefaultSkeleton.vue";
import PaginationSkeleton from "../Layout/Skeletons/PaginationSkeleton.vue";
import ArticleCardSkeleton from "../Layout/Skeletons/ArticleCardSkeleton.vue";

import Warning from "../Layout/Alerts/Warning.vue";
import Pagination from "../Layout/Pagination";
import Filters from "./Filters/Filters.vue";
import ArticleCard from './ArticleCard.vue';


export default
{
    /**
     * In SFCs, it's recommended to use PascalCase tag names for child components
     * to differentiate from native HTML elements.
     */
    components:{
        Filters,
        ArticleCard,
        Pagination,
        Warning,
        ListSkeleton,
        DefaultSkeleton,
        PaginationSkeleton,
        ArticleCardSkeleton,
    },

    /**
     * Component Props
     */
    props: {
        scrollTo: {
            type:String,
        },

        filterIcon: {
            type: String,
        },

        geo: {
            type: String,
            default: 'gb',
        }
    },

    /**
     * Component Data
     *
     * @return {{isLoading: boolean, perPage: number, strategyArticles: *[],
     *          filteredStrategyArticles: *[], filters: *[], currentPage: number}}
     */
    data()
    {
        return {
            strategyArticles: [],
            filteredStrategyArticles: [],
            filters: [],
            perPage: 9,
            currentPage: 1,
            isLoading: true,
        };
    },

    /**
     * Start fetching the data(async) when the component is created
     *
     * @return {Promise<void>}
     */
    async created()
    {
        this.getStrategyArticles();
    },

    /**
     * Computed Methods & properties
     */
    computed: {

        /**
         * Articles endpoint
         *
         * @return {string|string}
         */
        endpoint()
        {
            let geoR = this.geo.replace('-', '/');
            return this.geo === 'gb'
                ? '/json-files/site-article-strategy.json'
                : `/${geoR}/json-files/site-article-strategy.json`;
        },

        /**
         * Paginate articles
         *
         * @return {*[]}
         */
        articles()
        {
            let toTake = this.perPage * this.currentPage;
            let from = toTake - this.perPage;
            return this.filteredStrategyArticles.slice(from, toTake);
        },

        /**
         * Articles Count per page
         * @return {number}
         */
        articlesPerPage()
        {
            return Math.ceil(this.filteredStrategyArticles.length / this.perPage);
        },
        
        /**
         * No articles found text.
         * TODO: maybe move translation into it's own helpers functions...
         *
         * @return {*|string}
         */
        noArticlesLabel()
        {
            return (window.translations?.siteArticleStrategyFilter?.no_articles_found || 'No Articles Found...');
        },
    },
    methods: {

        /**
         * This function set the current page and scroll window to element #strategy-articles
         *
         * @param page
         * @return void
         */
        changePage(page)
        {
            this.currentPage = page;
            this.ktagRerender();

            // jump to top of the component
            let topOffset = window.pageYOffset + document.getElementById(this.scrollTo).getBoundingClientRect().top - 110;
            window.scrollTo({ top: topOffset });
        },

        /**
         * Get Articles using the fetch API
         * @return void
         */
        async getStrategyArticles()
        {
            fetch(window.location.origin + this.endpoint)
            .then((response) => response.json())
            .then((data) => {
                this.strategyArticles = data.articles;
                this.filteredStrategyArticles = data.articles;
                this.filters = data.filters;
                this.isLoading = false;
                this.ktagRerender();
            });
        },

        /**
         * @param input
         * @return void
         */
        getFilteredArticles(input)
        {
            this.currentPage = 1;
            this.filteredStrategyArticles = input;
            this.ktagRerender();
        },

        /**
         * Render kTags
         * @return void
         */
        ktagRerender()
        {
            setTimeout(
                () => window.ktag?.reRender(),
                500
            );
        },
    },
};
</script>

